import { IKeyArray } from 'types/IKeyArray';
import { IAnimation } from './animation';
import { IAudio, IAudioDAO } from './audio';
import { IBaseEntity, INode, IPortraitVariant, IPosition, IRotation } from './base';
import { IImage } from './image';
import { IMediaAsset } from './media';
import { IModel, IModelEntityConfig } from './model';
import { IModuleOut } from './module';
import { IScene, ISceneConfig, ISceneDAO } from './scene';
import { IText } from './text';
import { IVideo } from './video';
import { IUploadFile } from 'core/slices/upload';
import { IVariable, IVariableDAO } from './variable';

export interface IAction extends IBaseEntity {
  actionTemplateId: ActionType;
  name: string;
  sceneId: number;
  sceneConfigId: number;
  voiceSceneId?: number;
  voiceSceneConfigId?: number;
  mediaAssetId: number;
  childId: number;
  moduleId: number;
  textId: number;
  nodeId: number;
  type: ActionType;
  status: ActionStatus;
  value: number;
  variableId: number;
  variableOptionId: number;
  behaviour: ActionBehaviour;
  style: ActionStyle;
  timeLimit: number;
  timeScore: number;
  objectUrl?: string;
  showLogo?: boolean;
  sceneInteractive: boolean;
  entPos: ActionEntityPosition;
  pEntPos: ActionEntityPosition;
  allowSkip: boolean;
  allowDrag: boolean;
  allowRetake: boolean;
  visualFeedback: boolean;
  iconId?: number;
  emojiId?: string;
  uploadId?: string;
  youTubeId?: string;
}

export interface IActionBasic extends IAction {
  node: INode;
  text: IText;
  scene: IScene;
  sceneConfig: ISceneConfig;
  voiceSceneConfig?: ISceneConfig;
  variable: IVariable;
  isDecision?: boolean;
  isMultichoice?: boolean;
  entities: IActionEntityBasic[];
  outs: IKeyArray<IModuleOut>;
}

export interface IActionDAO extends IAction {
  node: INode;
  text: IText;
  type: ActionType;
  status: ActionStatus;
  entities: IActionEntityBasic[];
  scene: ISceneDAO;
  voiceScene: ISceneDAO;
  modelConfigs: IModelEntityConfig[];
  sceneConfig: ISceneConfig;
  voiceSceneConfig?: ISceneConfig;
  variable: IVariableDAO;
  outs: IKeyArray<IModuleOut>;
  start?: number;
  end?: number;
  modOut?: IModuleOut;
  parentActionId?: number;
  parentEntityId?: number;
  isDecision?: boolean;
  isMultichoice?: boolean;
  upload?: IUploadFile;
}

export interface IActionNode extends IActionDAO {
  depth: number;
  data: any;
  children: IActionNode[];
  index: number;
  source: string;
  sourceHandle: string;
  height: number;
  position: { x: number; y: number };
}

export enum ActionType {
  FUTURE = -2,
  PLACEHOLDER = -1,
  SIMPLE = 0,
  INTERACTIVE = 1,
  FLOAT = 2,
  TEXT_BLOCK_START = 3,
  TEXT_BLOCK_END = 4,
  OVERLAY_CENTER = 5,
  OVERLAY_START = 6,
  OVERLAY_END = 7,
  HIGHLIGHT_CENTER = 8,
  HIGHLIGHT_START = 9,
  HIGHLIGHT_END = 10,
  BUBBLE_START = 11,
  BUBBLE_END = 12,
  CONTAIN_CENTER = 13,
  CONTAIN_START = 14,
  CONTAIN_END = 15,
}

export const isSimple = (type: ActionType) => [ActionType.SIMPLE, ActionType.PLACEHOLDER].includes(type);
export const isBlock = (type: ActionType) => [ActionType.TEXT_BLOCK_START, ActionType.TEXT_BLOCK_END].includes(type);
export const isContain = (type: ActionType) =>
  [ActionType.CONTAIN_CENTER, ActionType.CONTAIN_START, ActionType.CONTAIN_END].includes(type);
export const isOverlay = (type: ActionType) =>
  [ActionType.OVERLAY_CENTER, ActionType.OVERLAY_START, ActionType.OVERLAY_END].includes(type);
export const isHighlight = (type: ActionType) =>
  [ActionType.HIGHLIGHT_CENTER, ActionType.HIGHLIGHT_END, ActionType.HIGHLIGHT_START].includes(type);
export const isBubble = (type: ActionType) => [ActionType.BUBBLE_START, ActionType.BUBBLE_END].includes(type);
export const isMediaAndText = (type: ActionType) =>
  isOverlay(type) ||
  isHighlight(type) ||
  [ActionType.INTERACTIVE].includes(type) ||
  isBlock(type) ||
  isBubble(type) ||
  isContain(type);

export const isStart = (type: ActionType) =>
  [
    ActionType.BUBBLE_START,
    ActionType.CONTAIN_START,
    ActionType.HIGHLIGHT_START,
    ActionType.OVERLAY_START,
    ActionType.TEXT_BLOCK_START,
  ].includes(type);

export const isEnd = (type: ActionType) =>
  [
    ActionType.BUBBLE_END,
    ActionType.CONTAIN_END,
    ActionType.HIGHLIGHT_END,
    ActionType.OVERLAY_END,
    ActionType.TEXT_BLOCK_END,
  ].includes(type);

export enum ActionBehaviour {
  PLAY = 0,
  PAUSE = 1,
}

export enum ActionStyle {
  DARK = 0,
  LIGHT = 1,
  BOX = 2,
  LINES = 3,
}

export enum ActionEntityPosition {
  CENTER = 0,
  TOP = 1,
  BOTTOM = 2,
  LEFT = 3,
  RIGHT = 4,
}

export const actionEntityPositionStyle = (pos: ActionEntityPosition) => {
  switch (pos) {
    case ActionEntityPosition.CENTER:
      return 'text-center';
    case ActionEntityPosition.LEFT:
      return 'text-left';
    case ActionEntityPosition.RIGHT:
      return 'text-right';
    default:
      return '';
  }
};

export enum ActionEntityType {
  BASIC = 0,
  TEXT = 1,
  BUTTON = 2,
  SHAPE = 3,
  // LINE = 4,
  // FROM_EMAIL = 5,
  // TO_EMAIL = 6,
  // MAILED_BY = 7,
  // SIGNED_BY = 8,
  // FROM_NAME = 9,
  // REPLY_TO = 10,
}

export enum ActionStatus {
  DELETED = -1,
  ACTIVE = 0,
}

export interface IActionTemplate extends IBaseEntity {
  name: string;
  type: number;
  status: number;
  icon: string;
  color: string;
}

export interface IActionAudio extends IBaseEntity {
  actionId: number;
  audioId: number;
  language: any;
  volume: number;
}

export interface IActionAudioDAO extends IBaseEntity {
  actionId: number;
  audio: IAudioDAO;
  language: any;
  volume: number;
}

export interface IActionAudio extends IBaseEntity {
  actionId: number;
  audioId: number;
  language: any;
  volume: number;
  positionId: number;
  start: number;
}

export interface IActionEntity extends IBaseEntity {
  actionId: number;
  name: string;
  textId: number;
  iconId: number;
  imageId: number;
  modelId: number;
  videoId: number;
  mediaAssetId: number;
  nodeId: number;
  referencePositionId: number;
  pPositionId: number;
  pRotationId: number;
  positionId: number;
  rotationId: number;
  hotspotId: number;
  audioId: number;
  color: string;
  correct: boolean;
  scale: number;
  opacity: number;
  order: number;
  points: any;
  width: number;
  pWidth: number;
  height: number;
  start: number;
  end: number;
  position?: IPosition;
  childActionId?: number;
  value?: number;
  variableOptionId?: number;
}

export interface IActionEntityDAO extends IActionEntity {
  actionId: number;
  text?: IText;
  iconId: number;
  audio: IAudio;
  image: IImage;
  video: IVideo;
  model: IModel;
  mediaAsset: IMediaAsset;
  position: IPosition;
  pPosition: IPosition;
  referencePosition: IPosition;
  rotation: IRotation;
  animations: IAnimation[];
  color: string;
  correct: boolean;
  sceneId?: number;
}

export interface IActionEntityBasic extends IActionEntity {
  actionId: number;
  text: IText;
  iconId: number;
  audio: IAudio;
  image: IImage;
  video: IVideo;
  model: IModel;
  mediaAsset: IMediaAsset;
  color: string;
  correct: boolean;
}

export interface IActionEntityLiveDAO extends IActionEntityDAO {
  isSelected?: boolean;
}
